import { useMediaQuery } from "@mui/material";
import React from "react";
import { theme } from "@/theme";
import { ImageAttributes } from "@/store";
import { Carousel } from "@/components/carousel/Carousel";
import { Button } from "@/components/Button";
import Rating from "@mui/material/Rating";
import { Star, StarBorder } from "@mui/icons-material";

export type UserTestimonial = {
  showReviewStars?: boolean;
  image: ImageAttributes;
  quoteTitle?: string;
  quote: string;
  author: string;
  buttonText?: string;
  buttonUrl?: string;
};

export type UserTestimonialsProps = {
  testimonials: UserTestimonial[];
};

export const UserTestimonials = ({
  testimonials = [],
}: UserTestimonialsProps) => {
  const isSmallScreen = useMediaQuery(
    theme.breakpoints.down(theme.breakpoints.values.md)
  );
  const isDesktopScreen = useMediaQuery(
    theme.breakpoints.up(theme.breakpoints.values.lg)
  );
  let items = testimonials.map((testimonial, i) => (
    <div
      className="flex flex-col items-center md:flex-row md:gap-10 lg:gap-16"
      key={i}
    >
      <div className="mb-8 w-full md:order-2 md:mb-0 md:basis-6/12 lg:flex-shrink-0 lg:basis-[35.85%]">
        <div className="mx-auto block max-w-sm md:max-w-md">
          <img
            src={testimonial.image.src}
            alt={testimonial.image.alt}
            width={testimonial.image.width}
            height={testimonial.image.height}
            loading={testimonial.image.loading}
            className="h-auto w-full"
          />
        </div>
      </div>
      <div className="text-center md:order-1 md:basis-6/12 md:text-left lg:flex-grow lg:basis-auto lg:py-[77.5px]">
        {testimonial?.showReviewStars && (
          <Rating
            sx={{ fontSize: "1.875rem" }}
            className="mb-4 text-primary"
            value={5}
            icon={<Star color="inherit" fontSize="inherit" />}
            emptyIcon={<StarBorder color="inherit" fontSize="inherit" />}
            max={5}
            readOnly
          />
        )}
        {testimonial.quoteTitle && (
          <p className="mb-4 text-3xl font-semibold text-primary md:text-5xl  xl:text-4xl">
            {testimonial.quoteTitle}
          </p>
        )}
        <p className="body text-md leading-normal">{testimonial.quote}</p>
        <div
          className="mt-5 flex flex-wrap items-center justify-center gap-x-4
        gap-y-2 md:justify-start"
        >
          <p className="title text-lg md:text-2xl">{testimonial.author}</p>
          <div className="flex h-[26px] items-center gap-1 rounded border border-solid border-primary bg-[#DCFFFB] px-2 py-1">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="13"
              height="13"
              fill="none"
            >
              <path
                fill="#37C9BE"
                d="M6.5.788c-3.308 0-6 2.692-6 6s2.692 6 6 6 6-2.692 6-6-2.692-6-6-6ZM9.853 5.21 6.02 9.014a.59.59 0 0 1-.827.015l-2.03-1.85a.61.61 0 0 1-.045-.842.595.595 0 0 1 .842-.03L5.568 7.78l3.428-3.429a.6.6 0 0 1 .857 0 .6.6 0 0 1 0 .857Z"
              />
            </svg>
            <span className="text-xs font-semibold">VERIFIED CUSTOMER</span>
          </div>
        </div>
        {testimonial.buttonText && (
          <Button
            data-id="testimonial-slide-btn"
            className="mt-8 sm:mt-14"
            href={testimonial.buttonUrl}
            size={isSmallScreen ? "sm" : "md"}
            component="a"
          >
            {testimonial.buttonText}
          </Button>
        )}
      </div>
    </div>
  ));

  return (
    <div className="mx-auto my-0 w-full">
      <Carousel
        data-id="user-testimonials-carousel"
        items={items}
        slideGap={false}
        speed={700}
        effect="slide"
        arrows={
          isSmallScreen
            ? "none"
            : {
                gutter: isDesktopScreen ? "xl" : "md",
                variant: "blue-icon",
              }
        }
        dots={{
          paddingOnWrapper: false,
          gutter: isDesktopScreen ? "none" : "lg",
        }}
      />
    </div>
  );
};
